import React, { useState } from 'react';

const MovementList = ({ movements, loading, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterTransaction, setFilterTransaction] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Filter movements
  const filteredMovements = movements.filter((movement) => {
    const matchesSearch =
      movement.itemDescription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      movement.itemCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      movement.jobcardNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      movement.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      movement.supplier?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = filterType === 'all' || movement.movementType === filterType;
    const matchesTransaction = filterTransaction === 'all' || movement.transactionType === filterTransaction;

    let matchesDate = true;
    if (dateFrom || dateTo) {
      const movementDate = movement.date?.toDate ? movement.date.toDate() : new Date(movement.date);
      if (dateFrom) {
        const fromDate = new Date(dateFrom);
        matchesDate = matchesDate && movementDate >= fromDate;
      }
      if (dateTo) {
        const toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999); // End of day
        matchesDate = matchesDate && movementDate <= toDate;
      }
    }

    return matchesSearch && matchesType && matchesTransaction && matchesDate;
  });

  // Calculate statistics
  const totalMovements = movements.length;
  const totalIn = movements.filter(m => m.movementType === 'IN').reduce((sum, m) => sum + (m.quantityIn || 0), 0);
  const totalOut = movements.filter(m => m.movementType === 'OUT').reduce((sum, m) => sum + (m.quantityOut || 0), 0);
  const totalValue = movements.reduce((sum, m) => sum + (m.totalValue || 0), 0);

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-NG', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const exportToCSV = () => {
    const headers = [
      'Date',
      'Item Code',
      'Item Description',
      'Movement Type',
      'Transaction Type',
      'Quantity In',
      'Quantity Out',
      'Balance After',
      'Job Card',
      'Vehicle Reg',
      'Customer',
      'Technician',
      'Supplier',
      'Remarks'
    ];

    const csvData = filteredMovements.map(m => [
      formatDate(m.date),
      m.itemCode || '',
      m.itemDescription || '',
      m.movementType || '',
      m.transactionType || '',
      m.quantityIn || 0,
      m.quantityOut || 0,
      m.balanceAfter || 0,
      m.jobcardNo || '',
      m.vehicleRegNo || '',
      m.customerName || '',
      m.technician || '',
      m.supplier || '',
      m.remarks || ''
    ]);

    const csv = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `movement-history-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-white"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <div className="bg-white/95 backdrop-blur-sm overflow-hidden shadow-lg rounded-xl p-4">
          <div className="flex items-center">
            <div className="p-3 bg-gradient-to-br from-gray-500 to-gray-600 rounded-xl">
              <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
              </svg>
            </div>
            <div className="ml-4 flex-1">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wider">Total Movements</p>
              <p className="text-2xl font-black text-gray-900">{totalMovements}</p>
            </div>
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur-sm overflow-hidden shadow-lg rounded-xl p-4">
          <div className="flex items-center">
            <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl">
              <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
              </svg>
            </div>
            <div className="ml-4 flex-1">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wider">Total IN</p>
              <p className="text-2xl font-black text-green-900">{totalIn.toFixed(1)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur-sm overflow-hidden shadow-lg rounded-xl p-4">
          <div className="flex items-center">
            <div className="p-3 bg-gradient-to-br from-red-500 to-rose-600 rounded-xl">
              <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 13l-5 5m0 0l-5-5m5 5V6" />
              </svg>
            </div>
            <div className="ml-4 flex-1">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wider">Total OUT</p>
              <p className="text-2xl font-black text-red-900">{totalOut.toFixed(1)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur-sm overflow-hidden shadow-lg rounded-xl p-4">
          <div className="flex items-center">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl">
              <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4 flex-1">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wider">Total Value</p>
              <p className="text-2xl font-black text-gray-900">₦{totalValue.toLocaleString('en-NG')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/95 backdrop-blur-sm shadow-lg rounded-xl p-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-5">
          {/* Search */}
          <div className="sm:col-span-2">
            <label htmlFor="search" className="sr-only">Search</label>
            <div className="relative rounded-xl shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                id="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-xl"
                placeholder="Search items, job cards, customers..."
              />
            </div>
          </div>

          {/* Movement Type Filter */}
          <div>
            <label htmlFor="filterType" className="sr-only">Filter by movement type</label>
            <select
              id="filterType"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm rounded-xl"
            >
              <option value="all">All Movements</option>
              <option value="IN">IN Only</option>
              <option value="OUT">OUT Only</option>
            </select>
          </div>

          {/* Transaction Type Filter */}
          <div>
            <label htmlFor="filterTransaction" className="sr-only">Filter by transaction</label>
            <select
              id="filterTransaction"
              value={filterTransaction}
              onChange={(e) => setFilterTransaction(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm rounded-xl"
            >
              <option value="all">All Transactions</option>
              <option value="PURCHASE">Purchase</option>
              <option value="ALLOCATION">Allocation</option>
              <option value="RETURN">Return</option>
              <option value="ADJUSTMENT">Adjustment</option>
              <option value="OFFICE_USE">Office Use</option>
              <option value="DAMAGE">Damage</option>
            </select>
          </div>

          {/* Export Button */}
          <div className="flex items-end">
            <button
              onClick={exportToCSV}
              className="w-full inline-flex justify-center items-center px-4 py-2 rounded-xl shadow-lg text-sm font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 transition-all transform hover:scale-105"
            >
              <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export
            </button>
          </div>
        </div>

        {/* Date Range */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 mt-4">
          <div>
            <label htmlFor="dateFrom" className="block text-xs font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              id="dateFrom"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
            />
          </div>
          <div>
            <label htmlFor="dateTo" className="block text-xs font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              id="dateTo"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
            />
          </div>
        </div>
      </div>

      {/* Movement Table */}
      <div className="bg-white/95 backdrop-blur-sm shadow-lg rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-emerald-500 to-teal-600">
              <tr>
                <th scope="col" className="px-2 py-1.5 text-left text-xs font-bold text-white uppercase tracking-wider">
                  Date
                </th>
                <th scope="col" className="px-2 py-1.5 text-left text-xs font-bold text-white uppercase tracking-wider">
                  Item
                </th>
                <th scope="col" className="px-2 py-1.5 text-left text-xs font-bold text-white uppercase tracking-wider">
                  Type
                </th>
                <th scope="col" className="px-2 py-1.5 text-center text-xs font-bold text-white uppercase tracking-wider">
                  Qty In
                </th>
                <th scope="col" className="px-2 py-1.5 text-center text-xs font-bold text-white uppercase tracking-wider">
                  Qty Out
                </th>
                <th scope="col" className="px-2 py-1.5 text-center text-xs font-bold text-white uppercase tracking-wider">
                  Balance
                </th>
                <th scope="col" className="px-2 py-1.5 text-left text-xs font-bold text-white uppercase tracking-wider">
                  Reference
                </th>
                <th scope="col" className="px-2 py-1.5 text-left text-xs font-bold text-white uppercase tracking-wider">
                  Remarks
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredMovements.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center">
                    <div className="flex flex-col items-center">
                      <svg className="h-12 w-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                      </svg>
                      <p className="text-sm font-medium text-gray-900">No movements found</p>
                      <p className="text-xs text-gray-500 mt-1">Try adjusting your filters</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredMovements.map((movement) => (
                  <tr key={movement.id} className="hover:bg-emerald-50/50 transition-colors">
                    <td className="px-2 py-1.5 whitespace-nowrap text-xs font-medium text-gray-700">
                      {formatDate(movement.date)}
                    </td>
                    <td className="px-2 py-1.5">
                      <div className="text-xs font-semibold text-gray-900">{movement.itemDescription}</div>
                      <div className="text-xs text-gray-500">{movement.itemCode}</div>
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <div className="flex flex-col space-y-1">
                        <span className={`px-2 py-0.5 inline-flex items-center text-xs leading-4 font-bold rounded-full ${
                          movement.movementType === 'IN'
                            ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white'
                            : 'bg-gradient-to-r from-red-500 to-rose-600 text-white'
                        }`}>
                          {movement.movementType === 'IN' ? '▲ IN' : '▼ OUT'}
                        </span>
                        <span className="text-xs text-gray-500 font-medium">{movement.transactionType}</span>
                      </div>
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-center">
                      <span className={`text-xs font-bold ${movement.quantityIn > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                        {movement.quantityIn > 0 ? `+${movement.quantityIn}` : '-'}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-center">
                      <span className={`text-xs font-bold ${movement.quantityOut > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                        {movement.quantityOut > 0 ? `-${movement.quantityOut}` : '-'}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-center text-xs font-bold text-gray-900">
                      {movement.balanceAfter}
                    </td>
                    <td className="px-2 py-1.5 text-xs text-gray-700">
                      {movement.jobcardNo && <div className="font-semibold">Job: {movement.jobcardNo}</div>}
                      {movement.customerName && <div className="text-xs text-gray-600">{movement.customerName}</div>}
                      {movement.supplier && <div className="font-semibold">Supplier: {movement.supplier}</div>}
                      {movement.technician && <div className="text-xs text-gray-600">Tech: {movement.technician}</div>}
                    </td>
                    <td className="px-2 py-1.5 text-xs text-gray-600 max-w-xs truncate">
                      {movement.remarks || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Results Summary */}
      <div className="text-sm text-emerald-100 text-center font-medium">
        Showing {filteredMovements.length} of {totalMovements} movements
      </div>
    </div>
  );
};

export default MovementList;
